import React from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { useRouter } from 'next/router';
import { Card, Table } from '@avila-tek/ui/src';
import { Hit } from '@algolia/client-search';
import { ApolloQueryResult, OperationVariables } from '@apollo/client';
import { Company, Goal, PageInfo, Period } from '../../models';
import { formatDateStringToISO } from '../../lib/utils';
import { findItemByText } from '../../lib';
import { useAlgoliaSearch, useUser } from '../../hooks';

const columnHelper = createColumnHelper<Period>();

const columns = [
  columnHelper.accessor('name', {
    header: 'Nombre',
  }),
  columnHelper.accessor('target', {
    header: 'Meta',
  }),
  columnHelper.accessor('unitTime', {
    header: 'Unidad de tiempo',
  }),
  columnHelper.accessor('startDate', {
    header: 'Fecha de inicio',
    cell: (info) => formatDateStringToISO(info.row.original.startDate),
  }),
  columnHelper.accessor('endDate', {
    header: 'Fecha de fin',
    cell: (info) => formatDateStringToISO(info.row.original.endDate),
  }),
  columnHelper.accessor('criterias', {
    header: 'Cantidad de criterios',
    cell: (info) => info.row.original.criterias.length,
  }),
];

interface PeriodTableProps {
  data: Period[];
  goalId: string | string[];
  refetch: (variables?: Partial<OperationVariables>) => Promise<
    ApolloQueryResult<{
      company: Company;
    }>
  >;
}

function PeriodTable({ data, goalId, refetch }: PeriodTableProps) {
  const router = useRouter();
  const [user] = useUser();

  const [periodsData, setPeriodsData] = React.useState(data);
  const [searchText, setSearchText] = React.useState<string | number>('');

  const onClickRow = (e: React.MouseEvent<HTMLTableRowElement, MouseEvent>) => {
    router.push(`/app/periods/${e.currentTarget.dataset.id}?goalId=${goalId}`);
  };

  const onClickAddButton = () =>
    router.push(`/app/periods/create?goalId=${goalId}`);

  const [pageInfoAlgolia, setPageInfoAlgolia] = React.useState<PageInfo>({
    currentPage: 0,
    hasNextPage: false,
    hasPreviousPage: false,
    itemCount: 0,
    pageCount: 0,
    perPage: 10,
  });

  const companyItem = user.company as Company;

  const { fetchData } = useAlgoliaSearch<Goal>({
    pageInfo: pageInfoAlgolia,
    setPageInfo: setPageInfoAlgolia,
    restrictSearchableAttributes: [
      'companyDetails.goals.periods.name',
      'companyDetails.goals.periods.unitTime',
      'companyDetails.goals.periods.target',
      'companyDetails.goals.periods.startDate',
      'companyDetails.goals.periods.endDate',
    ],
    index: 'companies',
    filters: `id:${companyItem?._id}`,
  });

  const memoizedSyncData = React.useCallback(async () => {
    if (searchText) {
      const { pageInfo, items } = await fetchData({
        searchText: searchText.toString(),
        newPage: pageInfoAlgolia.currentPage,
      });
      const goals = (items as Company)[0]?.companyDetails?.goals;
      const periods = goals.find((goal) => goal._id === goalId)?.periods;
      const filterPeriods = findItemByText(
        periods,
        searchText as string
      ) as Hit<Period>[];
      setPeriodsData(filterPeriods);
      setPageInfoAlgolia(pageInfo);
    } else {
      // Llama a refetch y espera su resultado
      const { data: refetchedData } = await refetch();

      if (refetchedData) {
        const goal = refetchedData.company?.companyDetails?.goals?.find(
          (goalItem) => goalItem._id === goalId
        );

        setPeriodsData((goal as Goal).periods ?? []);
      }
    }
  }, [pageInfoAlgolia?.currentPage, searchText, refetch]);

  React.useEffect(() => {
    memoizedSyncData();
  }, [searchText, memoizedSyncData]);

  return (
    <Card className="overflow-x-auto p-5">
      <Table<Period>
        data={periodsData}
        columns={columns}
        href="/app/periods/create"
      >
        <Table.Header>
          <Table.SearchInput
            manualFilter
            value={searchText.toString()}
            onChange={(value) => {
              setSearchText(value);
            }}
          />
          <Table.AddButton title="Período" action={onClickAddButton} />
        </Table.Header>
        <Table.Content<Goal> onClickRow={onClickRow} />
        <Table.Footer>
          <Table.Pagination className="ml-auto" />
        </Table.Footer>
      </Table>
    </Card>
  );
}

export default PeriodTable;
